import { Form } from "react-bootstrap";
import { useForm } from 'react-hook-form';
import { useRef } from 'react';
import dynamic from "next/dynamic";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaInstagram,
  FaFacebookF,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaUserAlt,
  FaPencilAlt,
  FaArrowRight,
} from "react-icons/fa";

// Lazy-load the enquiry modal — it pulls in framer-motion and a chunk of
// SCSS. Only needed when the user clicks "Send Enquiry".
const SendEnquiryButton = dynamic(
  () => import("@/components/enquiry/SendEnquiryButton"),
  { ssr: false }
);

const Contact = () => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const enquiryRef = useRef(null);

  const onSubmit = () => {
    toast.success("Your message has been sent. We'll get back to you soon!");
    reset();
  };

  const onSubmitWithPreventDefault = (event) => {
    event.preventDefault();
    handleSubmit(onSubmit)();
  };

  const infoCards = [
    {
      icon: <FaMapMarkerAlt />,
      title: "Visit Us",
      lines: ["Quarter No.1/3,731,  Raje Raghuji Nagar,  Opp. Kamala Nehru College,  Nagpur - 440009"],
      href: "https://www.google.com/maps?q=Kunal+Padole+Group,+Nagpur",
      external: true,
    },
    {
      icon: <FaPhoneAlt />,
      title: "Call Us",
      lines: ["+91 98232 60303"],
      href: "tel:+919823260303",
      external: false,
    },
    {
      icon: <FaEnvelope />,
      title: "Email Us",
      lines: ["mail@kunalpadole.com"],
      href: "mailto:mail@kunalpadole.com",
      external: false,
    },
  ];

  const socials = [
    {
      icon: <FaFacebookF />,
      label: "Facebook",
      href: "https://www.facebook.com/siddhivinayakinfrabuilders/",
    },
    {
      icon: <FaInstagram />,
      label: "Instagram",
      href: "https://www.instagram.com/siddhivinayakinfrabuilders/",
    },
  ];

  return (
    <>
      <ToastContainer />

      {/* PAGE SUBTEXT */}
      <section className="contact-heading">
        <div className="container">
          <div className="contact-heading__eyebrow">
            <span className="contact-heading__line"></span>
            <FaMapMarkerAlt className="contact-heading__pin" />
            <span className="contact-heading__line"></span>
          </div>
        </div>

        <style jsx>{`
          .contact-heading {
            text-align: center;
            padding-top: 30px;
            padding-bottom: 20px;
          }
          .contact-heading__eyebrow {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 14px;
          }
          .contact-heading__line {
            width: 40px;
            height: 2px;
            background: #e6631e;
            display: inline-block;
          }
          .contact-heading__pin {
            color: #e6631e;
            font-size: 20px;
          }
        `}</style>
      </section>

      {/* CONTACT ADDRESS CARDS */}
      <section className="contact-cards contact-cards--v2">
        <div className="container">
          <div className="row g-4 justify-content-center">
            {infoCards.map((card, idx) => (
              <div className="col-lg-4 col-md-6" key={idx}>
                <a
                  href={card.href}
                  {...(card.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="contact-card"
                  style={{ textDecoration: "none" }}
                >
                  <span className="contact-card__icon">{card.icon}</span>
                  <span className="contact-card__divider"></span>
                  <div className="contact-card__body">
                    <h2 className="contact-card__title">{card.title}</h2>
                    {card.lines.map((line, i) => (
                      <p className="contact-card__line" key={i}>
                        {line}
                      </p>
                    ))}
                  </div>
                  <span className="contact-card__arrow">
                    <FaArrowRight />
                  </span>
                </a>
              </div>
            ))}
          </div>
        </div>

        <style jsx>{`
  .contact-cards--v2 .contact-card {
    position: relative !important;
    display: flex !important;
    align-items: center !important;
    gap: 14px !important;
    padding: 22px 24px !important;
    padding-left: 28px !important;
    border-radius: 12px !important;
    border: 1px solid #efe9e1 !important;
    background: #ffffff !important;
    box-shadow: 0 3px 14px rgba(20, 20, 40, 0.06) !important;
    height: 100% !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    cursor: pointer !important;
    transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease !important;
  }
  .contact-cards--v2 .contact-card::before {
    content: "" !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 5px !important;
    height: 100% !important;
    background: #e6631e !important;
  }
  .contact-cards--v2 .contact-card:hover {
    transform: translateY(-4px) !important;
    box-shadow: 0 16px 30px rgba(230, 99, 30, 0.14) !important;
    border-color: #f4c199 !important;
  }
  .contact-cards--v2 .contact-card__icon {
    flex: 0 0 auto !important;
    align-self: center !important;
    width: 40px !important;
    height: 40px !important;
    font-size: 26px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    color: #e6631e !important;
    box-shadow: none !important;
    transition: transform 0.25s ease !important;
  }
  .contact-cards--v2 .contact-card:hover .contact-card__icon {
    transform: scale(1.08) !important;
  }
  .contact-cards--v2 .contact-card__divider {
    flex: 0 0 auto !important;
    width: 1px !important;
    align-self: stretch !important;
    margin: 4px 0 !important;
    background: #e6e2db !important;
  }
  .contact-cards--v2 .contact-card__body {
    flex: 1 1 auto !important;
    align-self: center !important;
    text-align: left !important;
    min-width: 0 !important;
  }
  .contact-cards--v2 .contact-card__title {
    font-size: 16px !important;
    font-weight: 600 !important;
    margin-bottom: 4px !important;
    color: #14142b !important;
  }
  .contact-cards--v2 .contact-card__line {
    font-size: 13px !important;
    line-height: 1.5 !important;
    margin-bottom: 0 !important;
    color: #4a4d55 !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
  }
  .contact-cards--v2 .contact-card__arrow {
    flex: 0 0 auto !important;
    align-self: center !important;
    width: 34px !important;
    height: 34px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 50% !important;
    background: #e6631e !important;
    color: #ffffff !important;
    font-size: 13px !important;
    box-shadow: 0 6px 14px rgba(230, 99, 30, 0.25) !important;
    transition: transform 0.25s ease, background 0.25s ease !important;
  }
  .contact-cards--v2 .contact-card:hover .contact-card__arrow {
    transform: translateX(3px) !important;
    background: #d1560f !important;
  }
`}</style>
      </section>

      {/* CONTACT FORM + MAP SIDE BY SIDE */}
      <section className="contact-form-section">
        <div className="container">
          <div className="row align-items-stretch g-4">
            <div className="col-lg-5">
              <div className="contact-map__wrap contact-map__wrap--side">
                <iframe
                  title="Our Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d940.1003646798224!2d79.11135052759509!3d21.127135776983216!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x99fd1a18969a66d%3A0x85129ae6ad3ee4f4!2sKUNAL%20PADOLE%20GROUP!5e0!3m2!1sen!2sin!4v1787306756110!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  loading="lazy"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            <div className="col-lg-7">
              <div className="contact-form-card contact-form-card--compact">
                <div className="contact-form-card__header">
                  <span className="contact-form-card__subtitle">Get In Touch</span>
                  <h2 className="contact-form-card__title">Send Us A Message</h2>
                  <p className="contact-form-card__desc">
                    Have a question or want to discuss a project? Drop us a line
                    and our team will get back to you within 24 hours.
                  </p>
                </div>

                <form id="contact-form" onSubmit={onSubmitWithPreventDefault}>
                  <div className="row">
                    <div className="col-xl-6">
                      <div className="contact-field">
                        <label className="contact-field__label" htmlFor="c-name">
                          Your Name *
                        </label>
                        <div className="contact-field__control">
                          <FaUserAlt className="contact-field__icon" />
                          <input
                            id="c-name"
                            type="text"
                            placeholder="Enter your name"
                            {...register("name", { required: true })}
                          />
                        </div>
                        {errors.name && (
                          <span className="contact-field__error">
                            This field is required
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-xl-6">
                      <div className="contact-field">
                        <label className="contact-field__label" htmlFor="c-email">
                          Email Address *
                        </label>
                        <div className="contact-field__control">
                          <FaEnvelope className="contact-field__icon" />
                          <input
                            id="c-email"
                            type="email"
                            placeholder="Enter email address"
                            {...register("email", { required: true })}
                          />
                        </div>
                        {errors.email && (
                          <span className="contact-field__error">
                            This field is required
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-xl-6">
                      <div className="contact-field">
                        <label className="contact-field__label" htmlFor="c-phone">
                          Phone Number *
                        </label>
                        <div className="contact-field__control">
                          <FaPhoneAlt className="contact-field__icon" />
                          <input
                            id="c-phone"
                            type="text"
                            placeholder="Enter phone number"
                            {...register("phone", { required: true })}
                          />
                        </div>
                        {errors.phone && (
                          <span className="contact-field__error">
                            This field is required
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="contact-field">
                        <label className="contact-field__label" htmlFor="c-message">
                          Your Message *
                        </label>
                        <div className="contact-field__control contact-field__control--textarea">
                          <FaPencilAlt className="contact-field__icon contact-field__icon--top" />
                          <textarea
                            id="c-message"
                            rows={4}
                            placeholder="Tell us how we can help…"
                            {...register("message", { required: true })}
                          />
                        </div>
                        {errors.message && (
                          <span className="contact-field__error">
                            This field is required
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="contact-form-card__footer">
                    <div className="contact-form-card__socials">
                      <span>Follow us:</span>
                      {socials.map((s, i) => (
                        <a
                          key={i}
                          href={s.href}
                          aria-label={s.label}
                          title={s.label}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {s.icon}
                        </a>
                      ))}
                    </div>
                    <SendEnquiryButton
                      ref={enquiryRef}
                      label="Send Message"
                      className="btn theme-btn-1 btn-effect-1"
                    />
                  </div>
                </form>
              </div>
            </div>


          </div>
        </div>

        <style jsx>{`
          .contact-form-card--compact {
            padding: 26px 28px !important;
            border-radius: 16px !important;
            border: 1px solid #eee0d6 !important;
            box-shadow: 0 10px 30px rgba(20, 20, 40, 0.06) !important;
          }
          .contact-form-card--compact .contact-form-card__header {
            margin-bottom: 18px !important;
          }
          .contact-form-card--compact .contact-form-card__title {
            font-size: 24px !important;
            margin-bottom: 6px !important;
          }
          .contact-form-card--compact .contact-form-card__desc {
            font-size: 14px !important;
            margin-bottom: 0 !important;
            text-align: center !important;
          }
          .contact-form-card--compact .contact-field {
            margin-bottom: 14px !important;
          }
          .contact-form-card--compact .contact-field__label {
            font-size: 13px !important;
            margin-bottom: 6px !important;
          }
          .contact-form-card--compact .contact-field__control {
            border-radius: 10px !important;
            border: 1px solid #e6e2db !important;
            background: #faf9f6 !important;
            transition: border-color 0.2s ease, box-shadow 0.2s ease !important;
          }
          .contact-form-card--compact .contact-field__control:focus-within {
            border-color: #e6631e !important;
            box-shadow: 0 0 0 3px rgba(230, 99, 30, 0.12) !important;
            background: #fff !important;
          }
          .contact-form-card--compact textarea {
            min-height: 90px !important;
          }
          .contact-form-card--compact .contact-form-card__footer {
            margin-top: 16px !important;
            padding-top: 16px !important;
            border-top: 1px dashed #e6e2db !important;
          }
          .contact-form-card--compact .btn {
            border-radius: 10px !important;
            padding: 12px 26px !important;
            transition: transform 0.2s ease, box-shadow 0.2s ease !important;
          }
          .contact-form-card--compact .btn:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 8px 18px rgba(230, 99, 30, 0.28) !important;
          }
          .contact-form-section {
            padding-bottom: 80px !important;
          }
          .contact-map__wrap--side {
            width: 100%;
            height: 100%;
            min-height: 400px;
            border-radius: 16px;
            overflow: hidden;
            border: 1px solid #eee0d6;
            box-shadow: 0 10px 30px rgba(20, 20, 40, 0.06);
          }
          @media (max-width: 991px) {
            .contact-map__wrap--side {
              min-height: 320px;
            }
               .contact-form-section {
              padding-bottom: 40px !important;
            }
          }
        `}</style>
      </section>
    </>
  );
  
};

export default Contact;